import { z } from "zod";
import { baseEntitySchema } from "./base";
import { paymentMethodSchema } from "./payment";

export const poPaymentTimingSchema = z.enum([
  "pay_in_advance",
  "pay_after",
  "pay_once_booked",
]);
export type POPaymentTiming = z.infer<typeof poPaymentTimingSchema>;

export const poPaymentStatusSchema = z.enum(["unpaid", "partially_paid", "paid"]);
export type POPaymentStatus = z.infer<typeof poPaymentStatusSchema>;

export const purchaseOrderStatusSchema = z.enum([
  "open",
  "closed",
  "blocked",
  "voided",
]);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

// Why a PO reached the terminal `voided` status: the vendor returned the miles and
// the money came back ("buyback"), or the order never happened at all ("cancelled").
// Absent on POs voided before this was tracked — treat undefined as "buyback".
export const poVoidTypeSchema = z.enum(["buyback", "cancelled"]);
export type POVoidType = z.infer<typeof poVoidTypeSchema>;

export const purchaseOrderSourceSchema = z.enum(["CRM", "Broker", "Friend", "Other"]);
export type PurchaseOrderSource = z.infer<typeof purchaseOrderSourceSchema>;

export const purchaseOrderDataSchema = z.object({
  // Optional so a PO can be unlinked/detached from its inventory.
  inventoryId: z.string().optional(),
  vendorId: z.string().optional(),
  vendorDisplayName: z.string().optional(),
  vendorEmail: z.string().optional(),
  source: purchaseOrderSourceSchema.default("CRM"),
  productId: z.string(),
  productName: z.string(),
  sourceDealId: z.string().optional(),
  candidateId: z.string().optional(),
  paymentTiming: poPaymentTimingSchema,
  paymentStatus: poPaymentStatusSchema,
  // Denormalized cleared total paid to the supplier (cleared, outgoing payments
  // on this PO and its linked invoices). Maintained by recalculateFinanceStatus
  // and synced to BigQuery for the inventory value report's Paid/Owed split.
  amountPaid: z.number().nonnegative().default(0),
  nextPaymentDate: z.date().optional(),
  okToPay: z.boolean().optional(),
  paymentMethod: paymentMethodSchema.optional(),
  paymentNotes: z.string().optional(),
  paymentDetails: z.string().optional(),
  amountBought: z.number().nonnegative(),
  buyPrice: z.number().nonnegative(),
  taxCost: z.number().nonnegative().optional(),
  totalAmount: z.number().nonnegative().optional(),
  buyCpm: z.number().nonnegative(),
  status: purchaseOrderStatusSchema,
  notes: z.string().optional(),
  closedAt: z.date().optional(),
  // Buyback / refund tracking. Original purchase figures (amountBought, buyPrice,
  // buyCpm, totalAmount) are preserved as an immutable record; buybacks accumulate here.
  milesReturned: z.number().nonnegative().optional(),
  refundAmount: z.number().nonnegative().optional(),
  voidedAt: z.date().optional(),
  voidReason: z.string().optional(),
  voidType: poVoidTypeSchema.optional(),
  sdrInfo: z.string().optional(),
  sdrAgentId: z.string().optional(),
});

export type PurchaseOrderData = z.infer<typeof purchaseOrderDataSchema>;
export const purchaseOrderSchema = baseEntitySchema.merge(purchaseOrderDataSchema);
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;
